import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { cleanParams } from '@/utils/cleanParams';

import { apiAddressBookFetch } from '..';
import { isImportSessionNotFound } from './errors';
import {
  ListSessionRowsParams,
  ProviderConnectionInfo,
  SESSION_ROWS_DEFAULT_PAGE_SIZE,
  SessionListItem,
  SessionRowsFeedFilters,
  SessionRowsPage,
  SessionSummary,
} from './types';

export const CONTACT_IMPORT_QUERY_KEYS = {
  sessions: 'contact-import-sessions',
  sessionById: 'contact-import-session-by-id',
  sessionRows: 'contact-import-session-rows',
  sessionRowsFeed: 'contact-import-session-rows-feed',
  providerConnections: 'contact-import-provider-connections',
};

// ==================== Session Queries ====================

/** Recent resolution sessions initiated by the current user in the current owner scope. */
export const _getImportSessions = (ownerScope: string) => {
  return queryOptions({
    queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessions, ownerScope],
    queryFn: () => apiAddressBookFetch<SessionListItem[]>('/contact-import/sessions'),
    refetchOnMount: true,
    retry: 1,
  });
};

/** Session summary: status, build/commit progress, bucket counts, footer. */
export const _getImportSessionById = (id?: string) => {
  return queryOptions({
    enabled: !!id,
    queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionById, id],
    queryFn: () => apiAddressBookFetch<SessionSummary>(`/contact-import/sessions/${id}`),
    refetchOnMount: true,
    retry: (failureCount, error) => failureCount < 1 && !isImportSessionNotFound(error),
  });
};

/** Keyset page of a session's previewed rows (optional bucket/search filters). */
export const _getImportSessionRows = (id?: string, params?: ListSessionRowsParams) => {
  const safeParams = cleanParams(params);

  return queryOptions({
    enabled: !!id,
    queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRows, id, ...Object.values(params || {})],
    queryFn: () =>
      apiAddressBookFetch<SessionRowsPage>(`/contact-import/sessions/${id}/rows`, {
        params: { pageSize: SESSION_ROWS_DEFAULT_PAGE_SIZE, ...safeParams },
      }),
    refetchOnMount: true,
    retry: 1,
  });
};

/** Paged feed for one bucket/status/search combo — distinct from `_getImportSessionRows`
 * (one-off fetches) since `afterPosition` here is a React Query `pageParam`, not part of the key. */
export const _getImportSessionRowsFeed = (id: string, filters: SessionRowsFeedFilters) => {
  const { bucket, status, search } = filters;

  return infiniteQueryOptions({
    queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRowsFeed, id, bucket, status, search],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      apiAddressBookFetch<SessionRowsPage>(`/contact-import/sessions/${id}/rows`, {
        params: cleanParams({
          bucket,
          status,
          search,
          afterPosition: pageParam,
          pageSize: SESSION_ROWS_DEFAULT_PAGE_SIZE,
        }),
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.nextAfterPosition ?? undefined) : undefined,
    retry: 1,
  });
};

// ==================== Provider connection queries ====================

/** Connection status (Connected/NeedsReauth/NotConnected) per OAuth-connectable provider. */
export const _getProviderConnections = (ownerScope: string) => {
  return queryOptions({
    queryKey: [CONTACT_IMPORT_QUERY_KEYS.providerConnections, ownerScope],
    queryFn: () => apiAddressBookFetch<ProviderConnectionInfo[]>('/contact-import/providers'),
    refetchOnMount: true,
    retry: 1,
  });
};
